

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const MerchantDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [merchant, setMerchant] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchMerchantData();
    }, [id]);

    const fetchMerchantData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/merchants/${id}`);
            setMerchant(response.data);
        } catch (error) {
            console.error('Error fetching merchant data:', error);
            setSnackbar({ open: true, message: 'Error loading merchant details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setMerchant(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/merchants/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Merchant updated successfully', severity: 'success' });
            fetchMerchantData();
        } catch (error) {
            console.error('Error updating merchant:', error);
            setSnackbar({ open: true, message: 'Error updating merchant', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this merchant?')) {
            try {
                await axiosInstance.delete(`/merchants/${id}`);
                setSnackbar({ open: true, message: 'Merchant deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/merchants'), 1000);
            } catch (error) {
                console.error('Error deleting merchant:', error);
                setSnackbar({ open: true, message: 'Error deleting merchant', severity: 'error' });
            }
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        <InlineEditField
                            label={field.label}
                            value={merchant?.[field.field]}
                            options={field.options || null}
                            onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                            isEditing={isEditing}
                            readOnly={field.readOnly || false}
                            renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!merchant) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Merchant not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {merchant.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Merchant Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchMerchantData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Merchant">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Merchant">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Merchant Information", [
                                    { label: "Merchant Name", field: "name" },
                                    { label: "Merchant Type", field: "merchant_type", options: [
                                        { value: "Accommodation", label: "Accommodation" },
                                        { value: "Activity", label: "Activity" },
                                        { value: "Transportation", label: "Transportation" },
                                        { value: "Meals", label: "Meals" },
                                        { value: "Misc.", label: "Misc." }
                                    ]},
                                    { label: "Website", field: "website" },
                                    { label: "Email", field: "email" },
                                    { label: "Phone", field: "phone" },
                                    { label: "Payment Currency", field: "payment_currency" },
                                    { label: "Payment Term", field: "payment_term", options: [
                                        { value: "NET 7", label: "NET 7" },
                                        { value: "NET 15", label: "NET 15" },
                                        { value: "NET 30", label: "NET 30" },
                                        { value: "NET 45", label: "NET 45" },
                                        { value: "NET 60", label: "NET 60" },
                                        { value: "NET 90", label: "NET 90" },
                                        { value: "Due on Receipt", label: "Due on Receipt" }
                                    ]},
                                    { label: "Partner", field: "is_partner", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }], renderValue: (val) => (val ? "Yes" : "No") },
                                    { label: "Operating Country", field: "operating_country" },
                                    { label: "Tax ID", field: "tax_id" },
                                    { label: "Tax Rate", field: "tax_rate", renderValue: (val) => val ? `${parseFloat(val).toFixed(2)}%` : '-' },
                                    { label: "Account", field: "account_id", readOnly: true, renderValue: (val) => merchant.account?.name || val || '-' },
                                ])}

                                {renderSection("Address Information", [
                                    { label: "Shipping Address", field: "shipping_address" },
                                    { label: "Billing Address", field: "billing_address" },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantDetail;

